import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { ToastContainer, toast } from "react-toastify";
import PropTypes from "prop-types";
import { apiCall } from "utils/apiClient";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import NontAuthorized401 from "NontAuthorized401";
import Pagination from "components/pagination";

const antIcon = <LoadingOutlined style={{ fontSize: 18 }} spin />;
const BASE_URL = process.env.REACT_APP_HAPS_MAIN_BASE_URL || "";
const MAIN_BANNER_TYPE = "main_banner";
const SERVICE_CATEGORY_BANNER_TYPE = "service_category_banner";

const resolveImageUrl = (value) => {
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  return `${BASE_URL.replace(/\/$/, "")}/${value.replace(/^\//, "")}`;
};

function MainBannerImages({ defaultTab = "all" }) {
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewModal, setPreviewModal] = useState(false);
  const [recordsPerPage, setRecordsPerPage] = useState(10);

  const [banners, setBanners] = useState({ desktop: [], mobile: [] });
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [currentPage, setCurrentPage] = useState(1);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addDeviceTarget, setAddDeviceTarget] = useState(1);
  const [addBannerType, setAddBannerType] = useState(MAIN_BANNER_TYPE);
  const [addServiceTypeId, setAddServiceTypeId] = useState("");
  const [serviceTypes, setServiceTypes] = useState([]);
  const [addFile, setAddFile] = useState(null);
  const [addLinkDraft, setAddLinkDraft] = useState("");
  const addFileInputRef = useRef(null);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editLinkDraft, setEditLinkDraft] = useState("");
  const [editFile, setEditFile] = useState(null);
  const editFileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
    fetchServiceTypes();
  }, []);

  const fetchData = async () => {
    setIsFetching(true);
    try {
      const result = await apiCall({
        endpoint: "banner/get-all",
        method: "GET",
      });

      const desktop = [];
      const mobile = [];

      (result?.data || []).forEach((item) => {
        const bannerObj = {
          id: item.id,
          path: item.path,
          image_index: Number(item.image_index) || 0,
          links: item.link ? [item.link] : [],
          banner_type: item.banner_type || item.bannerType || "",
          service_type_id:
            item.service_type_id ??
            item.serviceTypeId ??
            item?.service_type?.id ??
            "",
          service_type_name:
            item.service_type_name ||
            item.serviceTypeName ||
            item?.service_type?.name ||
            "",
        };

        if (Number(item.device) === 1) desktop.push(bannerObj);
        else mobile.push(bannerObj);
      });

      setBanners({ desktop, mobile });
    } catch (error) {
      toast.error("Failed to load banners");
    } finally {
      setIsFetching(false);
    }
  };

  const fetchServiceTypes = async () => {
    try {
      const result = await apiCall({
        endpoint: "services-type",
        method: "GET",
      });
      setServiceTypes(Array.isArray(result?.data) ? result.data : []);
    } catch (error) {
      setServiceTypes([]);
    }
  };

  // ✅ FIXED ALL TAB LOGIC
  const allRows = useMemo(() => {
    const desktopRows = banners.desktop.map((banner, index) => ({
      ...banner,
      deviceKey: "desktop",
      index,
    }));

    const mobileRows = banners.mobile.map((banner, index) => ({
      ...banner,
      deviceKey: "mobile",
      index,
    }));

    if (activeTab === "desktop") return desktopRows;
    if (activeTab === "mobile") return mobileRows;

    return [...desktopRows, ...mobileRows];
  }, [banners, activeTab]);

  const totalPages = Math.max(1, Math.ceil(allRows.length / recordsPerPage));
  const startIndex = (currentPage - 1) * recordsPerPage;
  const paginatedRows = allRows.slice(startIndex, startIndex + recordsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  const totals = useMemo(() => {
    return {
      imageCount: allRows.length,
      linkCount: allRows.reduce((sum, row) => sum + (row.links?.length || 0), 0),
    };
  }, [allRows]);

  const serviceTypeNameById = useMemo(() => {
    const map = {};
    serviceTypes.forEach((item) => {
      if (item?.id === undefined || item?.id === null) return;
      map[String(item.id)] = item?.name || item?.title || item?.service_type || "";
    });
    return map;
  }, [serviceTypes]);

  const handleAddSave = async () => {
    if (!addFile) return toast.error("Please select image");
    if (addBannerType === SERVICE_CATEGORY_BANNER_TYPE && !addServiceTypeId) {
      return toast.error("Please select service type");
    }

    setLoading(true);
    try {
      const formdata = new FormData();
      formdata.append("path", addFile);
      formdata.append("device", addDeviceTarget);
      formdata.append("banner_type", addBannerType);
      if (addBannerType === SERVICE_CATEGORY_BANNER_TYPE) {
        formdata.append("service_type_id", String(addServiceTypeId));
      }
      if (addLinkDraft) formdata.append("link", addLinkDraft);

      await apiCall({
        endpoint: "/banner/createBannerImage",
        method: "POST",
        data: formdata,
      });

      toast.success("Banner added");
      setAddModalOpen(false);
      setAddDeviceTarget(1);
      setAddBannerType(MAIN_BANNER_TYPE);
      setAddServiceTypeId("");
      setAddFile(null);
      setAddLinkDraft("");
      if (addFileInputRef.current) addFileInputRef.current.value = "";
      fetchData();
    } catch {
      toast.error("Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const deleteBanner = async (row) => {
    setLoading(true);
    try {
      await apiCall({
        endpoint: `/banner/deleteBanner/${row.id}`,
        method: "DELETE",
      });
      fetchData();
    } catch {
      toast.error("Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (row) => {
    setEditTarget(row);
    setEditLinkDraft(row.links?.[0] || "");
    setEditFile(null);
    setEditModalOpen(true);
  };

  const saveEditedBanner = async () => {
    if (!editTarget) return;

    setLoading(true);
    try {
      const formdata = new FormData();
      if (editFile) formdata.append("path", editFile);
      formdata.append(
        "device",
        editTarget.deviceKey === "desktop" ? 1 : 2
      );
      formdata.append("link", editLinkDraft);

      await apiCall({
        endpoint: "/banner/createBannerImage",
        method: "POST",
        data: formdata,
      });

      toast.success("Banner updated");
      setEditModalOpen(false);
      fetchData();
    } catch {
      toast.error("Update failed");
    } finally {
      setLoading(false);
    }
  };

  if (!localStorage.getItem("Token")) return <NontAuthorized401 />;

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <div className="p-4 space-y-4">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Main Banner Images
            </h2>
            <p className="text-sm text-gray-500">
              Manage desktop and mobile banners
            </p>
          </div>

          <button
            className="bg-black text-white px-4 py-2 rounded-lg"
            onClick={() => {
              setAddModalOpen(true);
              setAddDeviceTarget(1);
              setAddBannerType(MAIN_BANNER_TYPE);
              setAddServiceTypeId("");
              setAddFile(null);
              setAddLinkDraft("");
              if (addFileInputRef.current) addFileInputRef.current.value = "";
            }}
          >
            Add Image
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          {["all", "desktop", "mobile"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold ${
                activeTab === tab
                  ? "bg-black text-white"
                  : "bg-gray-200 text-gray-700"
              }`}
            >
              {tab.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow overflow-x-auto">
          {isFetching ? (
            <p className="p-6 text-center">Loading...</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2 text-left">#</th>
                  <th className="p-2 text-left">Preview</th>
                  <th className="p-2 text-left">Service Type</th>
                  <th className="p-2 text-left">Type</th>
                  <th className="p-2 text-left">URL</th>
                  <th className="p-2 text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRows.map((row, i) => {
                  const serviceTypeLabel =
                    row.service_type_name ||
                    (row.service_type_id
                      ? serviceTypeNameById[String(row.service_type_id)] || ""
                      : "");

                  return (
                    <tr key={row.id} className="border-t">
                      <td className="p-2">{startIndex + i + 1}</td>
                      <td className="p-2">
                        <img
                          src={resolveImageUrl(row.path)}
                          alt=""
                          className="h-12 w-20 object-cover rounded"
                        />
                      </td>
                      <td className="p-2">{serviceTypeLabel}</td>
                      <td className="p-2 capitalize">{row.deviceKey}</td>
                      <td className="p-2 truncate max-w-[250px]">
                        {row.links?.[0] || "--"}
                      </td>
                      <td className="p-2 text-center space-x-2">
                        <button
                          className="text-blue-600"
                          onClick={() => openEditModal(row)}
                        >
                          Edit
                        </button>
                        <button
                          className="text-red-600"
                          onClick={() => deleteBanner(row)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination Component */}
        <div className="mt-4">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            recordsPerPage={recordsPerPage}
            onRecordsPerPageChange={setRecordsPerPage}
            totalRecords={allRows.length}
            count={paginatedRows.length}
          showFirstLastControls
          compact
          />
        </div>

        <ToastContainer />

        {/* Add Modal */}
        <Modal
          open={addModalOpen}
          onCancel={() => {
            setAddModalOpen(false);
            setAddDeviceTarget(1);
            setAddBannerType(MAIN_BANNER_TYPE);
            setAddServiceTypeId("");
            setAddFile(null);
            setAddLinkDraft("");
            if (addFileInputRef.current) addFileInputRef.current.value = "";
          }}
          footer={null}
        >
          <div className="space-y-3">
            <select
              value={addDeviceTarget}
              onChange={(e) => setAddDeviceTarget(Number(e.target.value))}
              className="w-full border p-2 rounded"
            >
              <option value={1}>Desktop</option>
              <option value={2}>Mobile</option>
            </select>

            <select
              value={addBannerType}
              onChange={(e) => {
                const value = e.target.value;
                setAddBannerType(value);
                if (value !== SERVICE_CATEGORY_BANNER_TYPE) {
                  setAddServiceTypeId("");
                }
              }}
              className="w-full border p-2 rounded"
            >
              <option value={MAIN_BANNER_TYPE}>Main Banner</option>
              <option value={SERVICE_CATEGORY_BANNER_TYPE}>
                Service Category Banner
              </option>
            </select>

            {addBannerType === SERVICE_CATEGORY_BANNER_TYPE && (
              <select
                value={addServiceTypeId}
                onChange={(e) => setAddServiceTypeId(e.target.value)}
                className="w-full border p-2 rounded"
              >
                <option value="">Select Service Type</option>
                {serviceTypes.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name || item.title || `Service Type ${item.id}`}
                  </option>
                ))}
              </select>
            )}

            <input
              ref={addFileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => setAddFile(e.target.files[0])}
              className="w-full border p-2 rounded"
            />

            <input
              type="text"
              placeholder="URL"
              value={addLinkDraft}
              onChange={(e) => setAddLinkDraft(e.target.value)}
              className="w-full border p-2 rounded"
            />

            <button
              onClick={handleAddSave}
              disabled={loading}
              className="bg-black text-white px-4 py-2 rounded"
            >
              {loading ? <Spin indicator={antIcon} /> : "Save"}
            </button>
          </div>
        </Modal>

        {/* Edit Modal */}
        <Modal open={editModalOpen} onCancel={() => setEditModalOpen(false)} footer={null}>
          <div className="space-y-3">
            <select
              value={editTarget?.deviceKey === "desktop" ? 1 : 2}
              onChange={(e) => setEditTarget({ ...editTarget, deviceKey: e.target.value === "1" ? "desktop" : "mobile" })}
              className="w-full border p-2 rounded"
            >
              <option value={1}>Desktop</option>
              <option value={2}>Mobile</option>
            </select>

            <input
              ref={editFileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => setEditFile(e.target.files[0])}
              className="w-full border p-2 rounded"
            />

            <input
              type="text"
              value={editLinkDraft}
              onChange={(e) => setEditLinkDraft(e.target.value)}
              className="w-full border p-2 rounded"
            />

            <button
              onClick={saveEditedBanner}
              disabled={loading}
              className="bg-black text-white px-4 py-2 rounded"
            >
              {loading ? <Spin indicator={antIcon} /> : "Save"}
            </button>
          </div>
        </Modal>
      </div>
    </DashboardLayout>
  );
}

MainBannerImages.propTypes = {
  defaultTab: PropTypes.oneOf(["all", "desktop", "mobile"]),
};

export default MainBannerImages;
